
import React, { useState } from 'react';
import ProposalRealDataTable from '@/components/tables/ProposalRealDataTable';
import CollabNegotiationModal from '@/components/CollabNegotiationModal';
import DualPaymentModal from '@/components/DualPaymentModal';
import { CollabProposal } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';

interface ProposalPageProps {
  onNavigate: (view: string) => void;
}

const ProposalPage: React.FC<ProposalPageProps> = ({ onNavigate }) => {
  const { currentUser, token } = useAuth();
  const { notify } = useToast();
  const [isNegotiationModalOpen, setIsNegotiationModalOpen] = useState(false);
  const [isDualPaymentModalOpen, setIsDualPaymentModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<CollabProposal | null>(null);

  const handleNegotiate = (proposal: CollabProposal) => {
    setSelectedProposal(proposal);
    setIsNegotiationModalOpen(true);
  };

  const handleUpdateProposal = async (proposalId: string, updates: Partial<CollabProposal>, budgetChanged?: boolean) => {
    if (!token) {
        notify('Authentication token is missing. Please log in again.', 'error');
        return;
    }

    if (budgetChanged && updates.budget && selectedProposal) {
        const originalBudget = parseFloat(selectedProposal.budget);
        const newBudget = parseFloat(updates.budget);

        if (newBudget > originalBudget) {
            setSelectedProposal({ ...selectedProposal, ...updates });
            setIsDualPaymentModalOpen(true);
            setIsNegotiationModalOpen(false);
            return;
        }
    }

    try {
        const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updates),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update proposal');
        }
        notify('Proposal updated successfully!', 'success');
        setIsNegotiationModalOpen(false);
        // TODO: Figure out a way to refresh the table data
    } catch (error) {
        console.error('Error updating proposal:', error);
        notify((error as Error).message, 'error');
    }
  };

  const handleSendMessage = async (proposalId: string, message: string) => {
      console.log(`Message for ${proposalId}: ${message}`);
      notify('Chat functionality is under development.', 'info');
  };

  return (
    <div className="space-y-8 animate-in fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white mb-1">Proposal Management</h1>
        <button
          onClick={() => onNavigate('dashboard')}
          className="bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold py-2 px-4 rounded"
        >
          Back to Dashboard
        </button>
      </div>
      <div id="proposal-tables" className="grid grid-cols-1 gap-8 mt-8">
        <ProposalRealDataTable onNegotiate={handleNegotiate} />
      </div>
      <CollabNegotiationModal
        proposal={selectedProposal}
        isOpen={isNegotiationModalOpen}
        onClose={() => setIsNegotiationModalOpen(false)}
        onUpdate={handleUpdateProposal}
        onSendMessage={handleSendMessage}
        currentUser={currentUser}
      />
      {selectedProposal && (
        <DualPaymentModal
          isOpen={isDualPaymentModalOpen}
          onClose={() => setIsDualPaymentModalOpen(false)}
          proposal={selectedProposal}
          onPaymentSuccess={() => {
            setIsDualPaymentModalOpen(false);
            // TODO: Figure out a way to refresh the table data
          }}
        />
      )}
    </div>
  );
};

export default ProposalPage;
